import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import LenisProvider from "./components/LenisProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import RoomsPage from "./pages/RoomsPage";
import RoomDetailPage from "./pages/RoomDetailPage";
import ExperiencePage from "./pages/ExperiencePage";
import OffersPage from "./pages/OffersPage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";

// Admin
import AdminLayout from "./components/admin/AdminLayout";
import AdminLoginPage from "./pages/admin/LoginPage";
import AdminBookingsPage from "./pages/admin/BookingsPage";
import AdminBookingDetailPage from "./pages/admin/BookingDetailPage";
import AdminRoomsPage from "./pages/admin/RoomsPage";
import AdminPlaceholderPage from "./pages/admin/PlaceholderPage";

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      toastOptions={{
        style: {
          background: "var(--card)",
          border: "1px solid var(--border)",
          color: "var(--text)",
        },
      }}
    />
  );
}

function PublicShell({ children }) {
  return (
    <LanguageProvider>
      <SiteSettingsProvider>
        <LenisProvider>
          <Header />
          <main>{children}</main>
          <Footer />
        </LenisProvider>
      </SiteSettingsProvider>
    </LanguageProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            {/* Admin routes — no public Header/Footer, no Lenis smooth scroll. */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminBookingsPage />} />
              <Route path="bookings/:id" element={<AdminBookingDetailPage />} />
              <Route path="rooms" element={<AdminRoomsPage />} />
              <Route
                path="offers"
                element={
                  <AdminPlaceholderPage
                    title="Offerte"
                    hint="Gestirai pacchetti stagionali, sconti e periodi di validità direttamente da qui."
                  />
                }
              />
              <Route
                path="services"
                element={
                  <AdminPlaceholderPage
                    title="Servizi"
                    hint="Aggiungerai e modificherai i servizi hotel con icona Phosphor e descrizione bilingue."
                  />
                }
              />
              <Route
                path="experiences"
                element={
                  <AdminPlaceholderPage
                    title="Esperienze"
                    hint="Gestirai le esperienze torinesi con slug localizzati IT/EN e contenuto editoriale."
                  />
                }
              />
              <Route
                path="reviews"
                element={
                  <AdminPlaceholderPage
                    title="Recensioni"
                    hint="Aggiungerai manualmente o importerai recensioni da Google/TripAdvisor/Booking."
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <AdminPlaceholderPage
                    title="Impostazioni"
                    hint="Dati hotel, orari reception, social, link booking engine e metadati globali."
                  />
                }
              />
            </Route>

            {/* Public routes — wrapped with LanguageProvider + Header/Footer + Lenis */}
            <Route
              path="/"
              element={
                <PublicShell>
                  <Navigate to="/it" replace />
                </PublicShell>
              }
            />

            {/* Italian */}
            <Route path="/it" element={<PublicShell><HomePage /></PublicShell>} />
            <Route path="/it/camere" element={<PublicShell><RoomsPage /></PublicShell>} />
            <Route path="/it/camere/:slug" element={<PublicShell><RoomDetailPage /></PublicShell>} />
            <Route path="/it/esperienza-torino" element={<PublicShell><ExperiencePage /></PublicShell>} />
            <Route path="/it/offerte" element={<PublicShell><OffersPage /></PublicShell>} />
            <Route path="/it/servizi" element={<PublicShell><ServicesPage /></PublicShell>} />
            <Route path="/it/chi-siamo" element={<PublicShell><AboutPage /></PublicShell>} />
            <Route path="/it/contatti" element={<PublicShell><ContactPage /></PublicShell>} />

            {/* English */}
            <Route path="/en" element={<PublicShell><HomePage /></PublicShell>} />
            <Route path="/en/rooms" element={<PublicShell><RoomsPage /></PublicShell>} />
            <Route path="/en/rooms/:slug" element={<PublicShell><RoomDetailPage /></PublicShell>} />
            <Route path="/en/turin-experience" element={<PublicShell><ExperiencePage /></PublicShell>} />
            <Route path="/en/offers" element={<PublicShell><OffersPage /></PublicShell>} />
            <Route path="/en/services" element={<PublicShell><ServicesPage /></PublicShell>} />
            <Route path="/en/about" element={<PublicShell><AboutPage /></PublicShell>} />
            <Route path="/en/contact" element={<PublicShell><ContactPage /></PublicShell>} />

            <Route
              path="*"
              element={
                <PublicShell>
                  <Navigate to="/it" replace />
                </PublicShell>
              }
            />
          </Routes>
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
